import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  CartesianGrid,
} from "recharts";
import {
  Card,
  Col,
  Row,
  CardHeader,
  CardBody,
  CardFooter,
  Label,
  Button,
  DropdownItem,
  DropdownMenu,
  Dropdown,
  DropdownToggle,
} from "reactstrap";
import { Volume } from "react-feather";
import DateTimePicker from "react-datepicker";
import { useEffect, useState } from "react";
import { usePagination } from "../../hooks/usePagination";
import { format } from "date-fns";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import LeftIcon from "../svg/LeftButton";
import RightIcon from "../svg/RightIcon";

type chartProp = {
  fromDateTime?: string | null;
  toDateTime?: string | null;
  generateKey?: number | null | undefined;
  id?: number | string;
  selectedDashboard?: string | null;
};

const BarChar1 = ({
  fromDateTime,
  toDateTime,
  generateKey,
  id,
  selectedDashboard,
}: chartProp) => {
  const [isEnabled, setIsEnabled] = useState<any>(false);
  const [pumpingStationData, setPumpingStationData] = useState<any[]>();
  const [pumpingstation, setPumpingStation] = useState<any[]>();
  const [dropdownOpen, setDropdownOpen] = useState<boolean>();
  const [selectedPumpingStation, setSelectedPumpingStation] =
    useState<string>("");

  const [startIndex, setStartIndex] = useState(0);
  const windowSize = 8; // show 10 bars at a time

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 7);
  startDate.setHours(0, 0, 0, 0);

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const getPumpingStationHistory = async () => {
    try {
      const response = await ApiService.axiosApi(`/pumpingstation/history`, {
        method: "GET",
        params: {
          pumpingStationId: id ? id : 1,
          pageNumber: 0,
          pageSize: 0,
          fromDate:
            fromDateTime == null
              ? format(startDate, "yyyy-MM-dd HH:mm:ss")
              : format(fromDateTime, "yyyy-MM-dd HH:mm:ss "),
          toDate:
            toDateTime == null
              ? format(endDate, "yyyy-MM-dd HH:mm:ss ")
              : format(toDateTime, "yyyy-MM-dd HH:mm:ss "),
        },
      });
      if (response.success) {
        setPumpingStationData(response.data.data);
      } else {
        toast.error("Unable to get data at this moment");
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getPumpingStationHistory();
  }, [generateKey]);

  const handleNext = () => {
    if (
      pumpingStationData &&
      startIndex + windowSize < pumpingStationData.length
    ) {
      setStartIndex(startIndex + windowSize);
    }
  };

  const handlePrev = () => {
    if (startIndex - windowSize >= 0) {
      setStartIndex(startIndex - windowSize);
    }
  };

  const visibleData =
    pumpingStationData?.slice(startIndex, startIndex + windowSize) || [];

  return (
    <Card className="m-0 b-r-0 ">
      <CardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <span>
            <h5 style={{ fontWeight: "600" }}>Distribution Vs Pumping</h5>
            <h6 style={{ fontSize: "12px" }}>
              {" "}
              {fromDateTime
                ? format(new Date(fromDateTime), "dd/MM/yyyy HH:mm:ss")
                : format(startDate, "dd/MM/yyyy ")}{" "}
              -
              {toDateTime
                ? format(new Date(toDateTime), "dd/MM/yyyy HH:mm:ss")
                : format(endDate, "dd/MM/yyyy ")}
            </h6>
          </span>

          <div className="d-flex">
            <button
              className="icon-button "
              onClick={handlePrev}
              disabled={startIndex === 0}
            >
              <LeftIcon />
            </button>

            <button
              className="icon-button"
              onClick={handleNext}
              disabled={
                pumpingStationData &&
                startIndex + windowSize >= pumpingStationData.length
              }
            >
              <RightIcon />
            </button>
          </div>
        </div>
      </CardHeader>

      <CardBody>
        <ResponsiveContainer width="100%" height={305}>
          <BarChart data={visibleData} barCategoryGap="0%" barGap="5">
            <XAxis
              dataKey={"dateTime"}
              padding={{ left: 5, right: 5 }}
              tick={{ fontSize: 15, textAnchor: "centre" }}
              tickMargin={5}
              interval={1}
              tickFormatter={(value) => {
                const date = new Date(value);
                return date.toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  
                });
              }}
            />
            <YAxis
              label={{
                value: "Volume(m³)",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fontWeight: "bold" },
              }}
              domain={[0, 100]}
            />

            <Tooltip
                 
                 

              wrapperStyle={{
                border: "1px solid #ccc",
              }}
              formatter={(pumping: number, name: string) => [
                `${pumping}Volume(m³)`,
                name,
              ]}
              cursor={false}
              contentStyle={{
                backgroundColor: "white", // Tooltip box color
                borderRadius: "4px",
                border: "1px solid #ddd",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}

              labelFormatter={(value) => {
                const date = new Date(value);
                return format(date, "dd/MM/yyyy"); // no T
              }}
            />
            <Bar
              dataKey="dailyVolume"
              animationDuration={600}
              label={{ position: "top" }}
              radius={[0, 0, 0, 0]}
              barSize={30}
              fill="#DC3545"
            >
              {pumpingStationData?.map((entry: any, index) => (
                <Cell key={`cell-${index}`} />
              ))}
            </Bar>

            <Bar
              dataKey="distribution"
              animationDuration={600}
              label={{ position: "top" }}
              radius={[0, 0, 0, 0]}
              barSize={30}
              fill="#2B5F60"
            >
              {pumpingStationData?.map((entry: any, index) => (
                <Cell key={`cell-${index}`} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </CardBody>
      <CardFooter className="p-0">
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: "1rem",
            gap: "2rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "#DC3545",
                borderRadius: "2px",
              }}
            />
            <span>Pumping</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                backgroundColor: "#2B5F60",
                borderRadius: "2px",
              }}
            />
            <span>Distribution</span>
          </div>
        </div>
      </CardFooter>
    </Card>
  );
};

export default BarChar1;
